import { Money } from '@palaccount/money';

/**
 * The client-side mirror of FIN-03's allocation arithmetic — the drawing
 * layer of BR-FIN-03-06/-07/-09. The SERVER refuses an over-allocated write
 * outright; this mirror only decides the strip's colour and which rows to
 * paint red between keystrokes. Both tests use the half-cent tolerance
 * (BR-FIN-03-15), and nothing here is ever a JS number.
 */

/** N-13 / BR-FIN-03-15 — every money comparison is within half a cent. */
export const HALF_CENT = '0.005';

/** FIN-03 §5b — the four states of the validation strip. */
export type StripState = 'idle' | 'over' | 'credit' | 'full';

/**
 * THE WHOLE ALLOCATION, NOT THE PAGE OF IT ON SCREEN.
 *
 * This shape is the fix for PAGINATION.md §11's first finding, and the shape
 * is the fix rather than the discipline: the grid is paged now, and the old
 * signature took the ROWS BEING DRAWN. Handing it a page made `totalApplied`
 * and BR-FIN-03-09's `unallocated` describe fifty invoices while the strip
 * claimed they described the voucher — §3 step 10's quiet lie, stated in
 * money. A `Record` of every cell the editor is holding cannot be a page by
 * construction, so the wrong call is no longer writable.
 */
export interface AllocationSet {
  /**
   * Every allocated cell, keyed by document id, ACROSS EVERY PAGE — the
   * editor's own `alloc` state, which is complete whether or not the row is
   * being drawn. Values are exactly as typed; blank and half-typed cells
   * apply nothing.
   */
  readonly amounts: Readonly<Record<string, string>>;
  /**
   * Balance due per document id, for every row the grid has SEEN — the
   * whole-set balance source §11 asked for.
   *
   * A cell can only carry an amount two ways, and both are covered: a user
   * TYPED it, which means the row was drawn and its balance is in here; or
   * the server's oldest-first walk produced it, and that walk caps every row
   * at its own balance, so a row it produced cannot be over one. An id that
   * is missing here is therefore a row nothing can have over-allocated, and
   * the per-row test is SKIPPED for it rather than guessed at — the totals
   * below still count its money.
   */
  readonly balances: ReadonlyMap<string, string>;
}

export interface AllocationView {
  state: StripState;
  totalApplied: string;
  /** BR-FIN-03-09 — the excess held as customer credit ("0.00" when none). */
  unallocated: string;
  /** BR-FIN-03-06 — rows applying more than their invoice's balance. */
  overRows: ReadonlySet<string>;
}

const AMOUNT_RE = /^\d+(\.\d+)?$/;

function money(value: string, currency: string): Money | null {
  const trimmed = value.trim();
  if (!AMOUNT_RE.test(trimmed)) return null;
  try {
    return Money.of(trimmed, currency);
  } catch {
    return null;
  }
}

export function computeAllocationView(
  amountReceived: string,
  set: AllocationSet,
  currency = 'USD',
): AllocationView {
  const received = money(amountReceived, currency);

  let total = Money.zero(currency);
  const overRows = new Set<string>();

  // Over the ALLOCATION, not over the rows on screen — see `AllocationSet`.
  for (const [documentId, cell] of Object.entries(set.amounts)) {
    const amount = money(cell, currency);
    if (amount === null) continue; // empty/half-typed cells apply nothing
    total = total.plus(amount.rounded());
    const known = set.balances.get(documentId);
    const balance = known === undefined ? null : money(known, currency);
    // Per-row test: applied > balance due, beyond half a cent.
    if (balance !== null && amount.greaterThan(balance) && !amount.differsByLessThan(balance, HALF_CENT)) {
      overRows.add(documentId);
    }
  }

  const totalApplied = total.rounded();

  // "The amount received is still zero" — grey, before any allocation story.
  if (received === null || received.isZero(HALF_CENT)) {
    return {
      state: 'idle',
      totalApplied: totalApplied.toFixed2(),
      unallocated: '0.00',
      overRows,
    };
  }

  // Total test: applied > received, beyond half a cent — or any row over.
  const overInTotal =
    totalApplied.greaterThan(received) && !totalApplied.differsByLessThan(received, HALF_CENT);
  if (overInTotal || overRows.size > 0) {
    return {
      state: 'over',
      totalApplied: totalApplied.toFixed2(),
      unallocated: '0.00',
      overRows,
    };
  }

  const leftover = received.minus(totalApplied);
  if (!leftover.isZero(HALF_CENT)) {
    // Money left over is NOT an error: it becomes customer credit.
    return {
      state: 'credit',
      totalApplied: totalApplied.toFixed2(),
      unallocated: leftover.rounded().toFixed2(),
      overRows,
    };
  }

  return {
    state: 'full',
    totalApplied: totalApplied.toFixed2(),
    unallocated: '0.00',
    overRows,
  };
}

export interface OpenBalanceRow {
  invoiceId: string;
  balanceDue: string;
}

export interface AutoAllocateResult {
  /** invoiceId → amount to apply (2dp), only rows that receive money. */
  amounts: Record<string, string>;
  /** The (possibly written-back) amount received. */
  amountReceived: string;
}

/**
 * The amount to SEND to an auto-allocate route, or `undefined` for
 * BR-FIN-03-10's "pay everything off" shortcut.
 *
 * This is the one place the amount box's four readings are decided, so the two
 * editors cannot disagree about them: blank, half-typed (`12.`), and zero
 * WITHIN HALF A CENT all mean "fill everything and write the total back", and
 * anything else is a figure to spread. It is the same gate
 * `autoAllocateOldestFirst` applies below — kept here rather than left to each
 * caller, because the server's walk uses `isPositive()` and a caller that
 * forwarded a sub-half-cent amount raw would get a spread where the fold gave
 * a fill.
 *
 * The result is rounded to 2dp, which is what the fold wrote back for the same
 * input — so the box reads identically whichever path produced it.
 */
export function autoAllocateAmount(amountReceived: string, currency = 'USD'): string | undefined {
  const received = money(amountReceived, currency);
  if (received === null || received.isZero(HALF_CENT)) return undefined;
  return received.rounded().toFixed2();
}

/**
 * BR-FIN-03-10, mirrored in the browser.
 *
 * **The server owns this walk now** — both the saved voucher
 * (`POST /api/receipts/:receiptId/auto-allocate`) and the unsaved one
 * (`POST /api/receipts/auto-allocate-preview`) run it over the party's WHOLE
 * open set. This copy survives for the one case that needs no server at all:
 * an allocation grid with NO party picked, where the open set is empty by
 * construction and asking about a party nobody chose would be a 404 rather
 * than an answer.
 *
 * It is kept byte-identical to the walk it used to drive rather than deleted,
 * because its tests are the record of what the server route had to reproduce.
 *
 * Walk the invoices OLDEST FIRST — the caller passes them in the open-invoices
 * order, which is oldest first — applying the smaller of the balance and the
 * money left. With a zero (or empty) amount received, fill every invoice fully
 * and write the total back into the amount box.
 */
export function autoAllocateOldestFirst(
  amountReceived: string,
  invoices: readonly OpenBalanceRow[],
  currency = 'USD',
): AutoAllocateResult {
  const received = money(amountReceived, currency);
  const amounts: Record<string, string> = {};

  if (received === null || received.isZero(HALF_CENT)) {
    // The "pay everything off" shortcut.
    let total = Money.zero(currency);
    for (const invoice of invoices) {
      const balance = money(invoice.balanceDue, currency);
      if (balance === null || balance.isZero(HALF_CENT)) continue;
      const rounded = balance.rounded();
      amounts[invoice.invoiceId] = rounded.toFixed2();
      total = total.plus(rounded);
    }
    return { amounts, amountReceived: total.toFixed2() };
  }

  let left = received;
  for (const invoice of invoices) {
    if (left.isZero(HALF_CENT)) break;
    const balance = money(invoice.balanceDue, currency);
    if (balance === null || balance.isZero(HALF_CENT)) continue;
    const applied = (balance.lessThan(left) ? balance : left).rounded();
    amounts[invoice.invoiceId] = applied.toFixed2();
    left = left.minus(applied);
  }
  return { amounts, amountReceived: received.rounded().toFixed2() };
}
